import React, { ReactElement, useMemo } from 'react';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import EditForm from 'components/editForm/EditForm';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
    ValidateValueType,
    ValuesInterface,
} from 'components/interfaces/GeneralInterface';
import { getTranslationKey, useTranslation } from 'utils/localization';
import { getMinDate, isValidDate } from 'utils/helpers/datetime';
import { getLocaleDateString } from 'utils/helpers/getLocalDateString';
import { FieldTypeApiEnum } from 'submodules/api_middleware';
import Info from 'components/login/signup/Info';
import stylesBasicBlock from 'components/blocks/BasicDetailsBlock.module.scss';
import { EditFormDataInterface } from 'components/interfaces/EditFormInterface';
import { ROW_BREAK } from 'components/editForm/utils/items';
import { TNamespaceEnum } from 'types/types';
import { isAbove15 } from 'utils/index';
import { getTokenCookie } from 'utils/helpers/browser/cookies';
import { TokenPurpose } from 'utils/helpers/cookies.shared';

interface Props {
    goNext: () => void;
    mustBeMinor: boolean;
    values: ValuesInterface;
    countriesList: Option[];
    getFormData: (data: EditFormDataInterface) => void;
}

const SignUpInitialDetails = ({
    goNext,
    mustBeMinor,
    values,
    countriesList,
    getFormData,
}: Props): ReactElement => {
    const { t } = useTranslation();

    // Zjištění, zda je uživatel přihlášený
    const isUserLoggedIn = !!getTokenCookie(TokenPurpose.Access);

    const fields = useMemo<GroupEditFieldInterface[]>(
        (): GroupEditFieldInterface[] =>
            getFieldWithLocalization(t, mustBeMinor, countriesList, values),
        [countriesList]
    );

    const getSideBlock = () => {
        return (
            <div className={classNames(stylesBasicBlock.sizeP50)}>
                {isUserLoggedIn ? <Info /> : null}
            </div>
        );
    };

    return (
        <EditForm
            fields={fields}
            getSideBlock={getSideBlock}
            buttonTextKey={t('general.next')}
            action={goNext}
            getFormData={getFormData}
        />
    );
};

export default SignUpInitialDetails;

const getFieldWithLocalization = (
    t: TFunction,
    mustBeMinor: boolean,
    countriesList: Option[],
    values: ValuesInterface
): GroupEditFieldInterface[] => [
    {
        title: 'pages.basicDetails',
        fields: [
            {
                name: 'title',
                value: values['title'],
                label: getTranslationKey(TNamespaceEnum.signup, 'fields.title'),
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S10,
                labelSize: LabelSize.TOP,
                autocomplete: 'title',
            },
            ROW_BREAK,
            {
                name: 'first_name',
                value: values['first_name'],
                label: 'address.name',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
                autocomplete: 'given-name',
            },
            {
                name: 'middle_name',
                value: values['middle_name'],
                label: 'address.middleName',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                autocomplete: 'additional-name',
            },
            {
                name: 'last_name',
                value: values['last_name'],
                label: 'address.surname',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
                autocomplete: 'family-name',
            },

            {
                name: 'birth_year',
                value: values['birth_year'],
                label: 'MemberProfile.birthDate',
                type: FieldTypeApiEnum.DATE,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                min: getMinDate(),
                max: new Date().getTime(),
                required: true,
                validate: (value: ValidateValueType) => {
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const date = new Date(value as string);

                    return isValidDate(date)
                        ? value
                            ? date.getTime() < now.getTime()
                                ? mustBeMinor && isAbove15(date)
                                    ? t('validations.minorDateBirthOnly')
                                    : undefined
                                : t('validations.dateMustBeInPast')
                            : undefined
                        : t('validations.dateUseFormat', {
                              format: getLocaleDateString(),
                          });
                },
                autocomplete: 'bday',
            },
            {
                name: 'nationality',
                value: values['nationality'],
                label: getTranslationKey(TNamespaceEnum.person, 'nationality'),
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.S20,
                placeholder: 'filters.choose',
                values: countriesList,
                labelSize: LabelSize.TOP,
                required: true,
            },
        ],
    },
];
